import { ProductResponse, Product as ProductType } from "@/services/product/product";
import type {
  Product,
  PaginatedResponse,
  SearchFilters,
  ApiResponse,
  Review,
} from "../../types";
import { apiService } from "../apiService";

/**
 * Product Service
 *
 * Handles all product-related API operations including:
 * - Fetching products with pagination and filters
 * - Getting individual products
 * - Searching products
 * - Managing product reviews
 * - Category-based product queries
 *
 * @example
 * ```typescript
 * // Get all products
 * const products = await productService.getProducts();
 *
 * // Get products with filters
 * const filteredProducts = await productService.getProducts({
 *   category: 'electronics',
 *   priceRange: { min: 100, max: 500 },
 *   sortBy: 'price',
 *   sortOrder: 'asc'
 * });
 *
 * // Get single product
 * const product = await productService.getProduct('product-123');
 *
 * // Search products
 * const searchResults = await productService.searchProducts('laptop');
 * ```
 */
export const productService = {
  /**
   * Get discounted products (Flash Sales)
   *
   * @param page - Page number (default: 1)
   * @param limit - Number of products per page (default: 10)
   * @returns Promise<ProductResponse> - Paginated list of discounted products
   *
   * @example
   * ```typescript
   * const flashSales = await productService.getDiscountedProducts(1, 10);
   * flashSales.data.products.forEach(product => {
   *   console.log(`${product.name}: ${product.discount.value}% off`);
   * });
   * ```
   */
  async getDiscountedProducts(
    page: number = 1,
    limit: number = 10
  ): Promise<ProductResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/products/discounted?${params.toString()}`;
    const response = await apiService.get<ProductResponse>(url);

    // Guard against undefined data
    if (!response?.success || !response?.data) {
      throw new Error("Invalid response from discounted products endpoint");
    }

    // Ensure products array exists
    if (!Array.isArray(response.data.products)) {
      response.data.products = [];
    }

    // Ensure pagination exists with defaults
    if (!response.data.pagination) {
      response.data.pagination = {
        currentPage: page,
        totalPages: 1,
        totalProducts: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: limit,
      };
    }

    // Ensure filters exists with defaults
    if (!response.data.filters) {
      response.data.filters = {
        category: "",
        priceRange: { min: "0", max: "no limit" },
        sortBy: "newest",
        status: "active",
        visibility: "visible",
      };
    }

    return response;
  },

  /**
   * Get a single product by ID
   *
   * @param id - Product ID
   * @returns Promise<Product> - Product details
   *
   * @example
   * ```typescript
   * const product = await productService.getProduct('product-123');
   * console.log(product.title); // "iPhone 15 Pro"
   * ```
   */
  async getProduct(id: string): Promise<ProductType> {
    if (!id) {
      throw new Error("Product ID is required");
    }

    const url = `/api/products/${id}`;
    const response = await apiService.get<ProductType>(url);

    return response;
  },

  /**
   * Get products by category with optional filters
   *
   * @param categoryName - Category Name
   * @param filters - Optional search and filter parameters
   * @returns Promise<PaginatedResponse<Product>> - Paginated list of products in category
   *
   * @example
   * ```typescript
   * const electronics = await productService.getProductsByCategory('electronics', {
   *   sortBy: 'price',
   *   sortOrder: 'asc'
   * });
   * ```
   */
  async getProductsByCategory(
    categoryName: string,
    filters?: SearchFilters
  ): Promise<ProductResponse> {
    if (!categoryName) {
      throw new Error("Category ID is required");
    }

    const params = new URLSearchParams({ category: categoryName });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const url = `/api/products/all?${params.toString()}`;
    return apiService.get<ProductResponse>(url);
  },

  /**
   * Get products by category and subcategory
   *
   * @param categoryId - Category ID
   * @param subcategoryId - Subcategory ID
   * @param filters - Optional search and filter parameters
   * @returns Promise<PaginatedResponse<Product>> - Paginated list of products in subcategory
   *
   * @example
   * ```typescript
   * const smartphones = await productService.getProductsByCategoryAndSubcategory(
   *   'electronics',
   *   'smartphones',
   *   { sortBy: 'price' }
   * );
   * ```
   */
  async getProductsByCategoryAndSubcategory(
    categoryName: string,
    subcategoryName: string,
    filters?: SearchFilters
  ): Promise<ProductResponse> {
    if (!categoryName) {
      throw new Error("Category ID is required");
    }
    if (!subcategoryName) {
      throw new Error("Subcategory ID is required");
    }

    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const url = `/api/products/category/${categoryName}/subcategory/${subcategoryName}?${params.toString()}`;
    return apiService.get<ProductResponse>(url);
  },

  /**
   * Search products by query with optional filters
   *
   * @param query - Search query string
   * @param filters - Optional search and filter parameters
   * @returns Promise<PaginatedResponse<Product>> - Paginated search results
   *
   * @example
   * ```typescript
   * const results = await productService.searchProducts('laptop', {
   *   priceRange: { min: 500, max: 2000 },
   *   rating: 4
   * });
   * ```
   */
  async searchProducts(
    query: string,
    filters?: SearchFilters
  ): Promise<PaginatedResponse<Product>> {
    if (!query?.trim()) {
      throw new Error("Search query is required");
    }

    const params = new URLSearchParams({ q: query.trim() });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const url = `/api/products/search?${params.toString()}`;
    return apiService.get<PaginatedResponse<Product>>(url);
  },

  /**
   * Get reviews for a specific product
   *
   * @param productId - Product ID
   * @returns Promise<ApiResponse<Review[]>> - Product reviews
   *
   * @example
   * ```typescript
   * const reviews = await productService.getProductReviews('product-123');
   * reviews.data.forEach(review => {
   *   console.log(`${review.userName}: ${review.rating}/5`);
   * });
   * ```
   */
  async getProductReviews(productId: string): Promise<ApiResponse<Review[]>> {
    if (!productId) {
      throw new Error("Product ID is required");
    }

    const url = `/api/products/${productId}/reviews`;
    return apiService.get<ApiResponse<Review[]>>(url);
  },

  /**
   * Add a review to a product
   *
   * @param productId - Product ID
   * @param review - Review data (without id, productId, createdAt, updatedAt)
   * @returns Promise<ApiResponse<Review>> - Created review
   *
   * @example
   * ```typescript
   * const newReview = await productService.addProductReview('product-123', {
   *   rating: 5,
   *   title: 'Great product!',
   *   comment: 'Highly recommended',
   *   userName: 'John Doe'
   * });
   * ```
   */
  async addProductReview(
    productId: string,
    review: Omit<Review, "id" | "productId" | "createdAt" | "updatedAt">
  ): Promise<ApiResponse<Review>> {
    if (!productId) {
      throw new Error("Product ID is required");
    }

    if (!review.rating || review.rating < 1 || review.rating > 5) {
      throw new Error("Rating must be between 1 and 5");
    }

    const url = `/api/products/${productId}/reviews`;
    return apiService.post<ApiResponse<Review>>(url, review);
  },

  /**
   * Get featured products (for homepage, etc.)
   *
   * @param limit - Number of featured products to return
   * @returns Promise<Product[]> - Featured products
   *
   * @example
   * ```typescript
   * const featured = await productService.getFeaturedProducts(6);
   * ```
   */
  async getFeaturedProducts(limit: number = 6): Promise<Product[]> {
    const url = `/api/products/featured?limit=${limit}`;
    return apiService.get<Product[]>(url);
  },

  

  /**
   * Get personalized products for authenticated users
   *
   * @returns Promise<ProductResponse> - Personalized products based on user preferences
   *
   * @example
   * ```typescript
   * const personalized = await productService.getPersonalizedProducts();
   * ```
   */
  async getPersonalizedProducts(): Promise<ProductResponse> {
    const url = `/api/products/personalized`;
    const response = await apiService.get<{ success: boolean; data: ProductType[] }>(url);

    // Guard against undefined data
    if (!response?.success || !response?.data) {
      throw new Error("Invalid response from personalized products endpoint");
    }

    // Ensure products array exists
    const products = Array.isArray(response.data) ? response.data : [];

    // Transform to ProductResponse format
    return {
      success: response.success,
      data: {
        products,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalProducts: products.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: products.length,
        },
        filters: {
          category: "",
          priceRange: { min: "0", max: "no limit" },
          sortBy: "newest",
          status: "active",
          visibility: "visible",
        },
      },
    };
  },

  /**
   * Get trending products for non-authenticated users
   *
   * @returns Promise<ProductResponse> - Trending products
   *
   * @example
   * ```typescript
   * const trending = await productService.getTrendingProducts();
   * ```
   */
  async getTrendingProducts(limit: number = 6): Promise<ProductResponse> {
    const url = `/api/products/trending?limit=${limit}`;
    const response = await apiService.get<{ success: boolean; data: ProductType[] }>(url);

    // Guard against undefined data
    if (!response?.success || !response?.data) {
      throw new Error("Invalid response from trending products endpoint");
    }

    // Ensure products array exists
    const products = Array.isArray(response.data) ? response.data : [];

    // Transform to ProductResponse format
    return {
      success: response.success,
      data: {
        products,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalProducts: products.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: products.length,
        },
        filters: {
          category: "",
          priceRange: { min: "0", max: "no limit" },
          sortBy: "newest",
          status: "active",
          visibility: "visible",
        },
      },
    };
  },

/**
 * Add a product to wishlist
 *
 * @param productId - Product ID to add to wishlist
 * @returns Promise<ApiResponse> - Success response
 */
async addToWishlist(productId: string): Promise<{ success: boolean; message: string; data?: any }> {
  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    const url = `/api/v2/buyers/wishlist`;
    const response = await apiService.post<{ success: boolean; message: string; data?: any }>(
      url, 
      { productId }
    );
    
    return {
      success: response.success ?? true,
      message: response.message || 'Added to wishlist',
      data: response.data,
    };
  } catch (error: any) {
    console.error('Add to wishlist error:', error);
    throw {
      message: error?.response?.data?.message || error?.message || 'Failed to add to wishlist',
      response: error?.response,
    };
  }
},

/**
 * Remove a product from wishlist
 *
 * @param productId - Product ID to remove from wishlist
 * @returns Promise<ApiResponse> - Success response
 */
async removeFromWishlist(productId: string): Promise<{ success: boolean; message: string; data?: any }> {
  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    // Try the standard DELETE endpoint first
    const url = `/api/v2/buyers/wishlist/${productId}`;
    const response = await apiService.delete<{ success: boolean; message: string; data?: any }>(url);
    
    return {
      success: response.success ?? true,
      message: response.message || 'Removed from wishlist',
      data: response.data,
    };
  } catch (error: any) {
    console.error('Remove from wishlist error:', error);
    
    throw {
      message: error?.response?.data?.message || error?.message || 'Failed to remove from wishlist',
      response: error?.response,
    };
  }
},

/**
 * Get all wishlist products with full details
 *
 * @returns Promise<ApiResponse> - List of wishlist products with full details
 */
async getWishlistProducts(): Promise<{ success: boolean; data: any[] }> {
  try {
    const url = `/api/v2/buyers/wishlist`;
    const response = await apiService.get<{ success: boolean; data: any }>(url);
    
    // Handle multiple possible response formats
    let products = [];
    
    if (Array.isArray(response.data)) {
      products = response.data;
    } else if (response.data?.products && Array.isArray(response.data.products)) {
      products = response.data.products;
    } else if (response.data?.wishlist && Array.isArray(response.data.wishlist)) {
      products = response.data.wishlist;
    }
    
    // Ensure each product has an id field
    products = products.map((product: any) => ({
      ...product,
      id: product.id || product._id,
    }));
    
    return {
      success: response.success ?? true,
      data: products,
    };
  } catch (error: any) {
    console.error('Failed to fetch wishlist products:', error);
    
    // Return empty array instead of throwing to prevent UI breakage
    return {
      success: false,
      data: [],
    };
  }
}
  
};

